import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';

import { AppUpdateWatcher } from '@repo/features/app-version';
import { PageLoading } from '@repo/features/page-loading';
import { PageNotFound } from '@repo/features/page-not-found';
import { ToastViewport } from '@repo/features/toast';
import { parseHttpError } from '@repo/utils/parse-http-error';

import { appVersion } from '~/app-version.ts';
import { Login } from '~/features/auth/login';
import { NoAccess } from '~/features/auth/no-access.tsx';
import { useFeatureGuard } from '~/features/auth/use-feature-guard.ts';
import { LayoutBasePage } from '~/features/layout-base-page/layout-base-page.tsx';
import { PageError } from '~/features/page-error/page-error.tsx';
import { useMatchedRoute } from '~/hooks/use-matched-route.ts';
import { queryCurrentUser } from '~/services/authorization/authorization.ts';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: PageNotFound,

});

function RootComponent() {
  const isAuthenticated = useIsAuthenticated();
  const match = useMatchedRoute();

  return (
    <>
      <title>
        {`${match.title} | Northern Lights`}
      </title>
      {!isAuthenticated ? <Login /> : <Layout />}
      <ToastViewport />
      <AppUpdateWatcher currentVersion={appVersion} />
    </>
  );
}

function Layout() {
  const router = useRouter();
  const account = useAccount();
  const { instance } = useMsal();
  const match = useMatchedRoute();
  const navigate = Route.useNavigate();
  const { hasSomeFeature, isLoading } = useFeatureGuard();
  const {
    data: user,
    isSuccess,
    error,
    refetch,
  } = useQuery(queryCurrentUser());

  useEffect(() => {
    if (error && parseHttpError(error).status === 401) {
      void instance.logoutRedirect(account ? { account } : undefined);
    }
  }, [instance, error, account]);

  useEffect(() => {
    if (match.path === '/' && user) {
      void navigate({ to: '/organization/$org', params: { org: user.organizationId }, replace: true });
    }
  }, [match.path, navigate, user]);

  if (error && parseHttpError(error).status !== 401) {
    return (
      <div className="flex items-center justify-center p-8 w-full">
        <PageError
          msg="An error occurred while loading user information."
          retry={async () => {
            await refetch();
            await router.invalidate();
          }}
        />
      </div>
    );
  }

  if (!isSuccess) {
    return (
      <div className="flex items-center justify-center p-8 pt-20 w-full">
        <PageLoading />
      </div>
    );
  }

  return (
    <LayoutBasePage>
      {hasSomeFeature(match.features)
        ? <Outlet />
        : isLoading
          ? <PageLoading />
          : <NoAccess />}
    </LayoutBasePage>
  );
}
